import {
    IOrganizationCreditPaymentMethodFields,
    IOrganizationCreditPaymentMethodViewModel
} from "./organization-credit-payment-method.view-model.interface";
import {BookingModel} from "../../../booking.model";
import {makeObservable, observable, runInAction} from "mobx";
import {Price} from "../../../../../currency/price";
import {FormModel} from "../../../../../../models/forms/form.model";
import {FormFields, IFormField} from "../../../../../../models/forms/form-field.interface";
import {BookingSessionStorageKeys} from "../../../storage/booking-session-storage-keys";
import {IAuxiliaryPaymentAddToBookingResult, IBookingAuxiliaryPayment} from "../booking-auxiliary-payment.interface";


export class OrganizationCreditPaymentMethod extends FormModel<IOrganizationCreditPaymentMethodFields> implements IOrganizationCreditPaymentMethodViewModel, IBookingAuxiliaryPayment {
    constructor(private readonly booking: BookingModel) {
        super(booking.services)
        this._available = booking.createPrice(0);
        makeObservable<this, '_available'>(this, {
            _available: observable.ref
        })
    }

    protected _createFields(): FormFields<IOrganizationCreditPaymentMethodFields> {
        const persistedFields = this._getPersistedFields();
        return {
            useAgencyFunds: this._createField<boolean>({
                fieldName: () => this.services.language.translate('Agency funds'),
                isRequired: false,
                defaultValue: persistedFields.useAgencyFunds,
            })
        }
    }

    protected _onFieldChanged(field: IFormField) {
        super._onFieldChanged(field);
        this._persistFields();
    }

    get isDisabled(): boolean {
        return this.booking.paymentHandler.holdBooking || this.maxAllowed === 0;
    }

    get useAgencyCredit(): boolean {
        if(this.isDisabled) {
            return false;
        }
        return this.fields.useAgencyFunds.value ?? false;
    }

    set useAgencyCredit(value: boolean) {
        this.fields.useAgencyFunds.setValue(value);
    }

    async loadOrganizationCredit(): Promise<void> {
        try {
            const amount = await this.services.user.profile.getOrganizationCreditForBooking(this.booking.currency);
            runInAction(() => {
                this._available = this.booking.createPrice(amount);
            });
        } catch (err) {
            this.services.logger.error('Failed to load organization credit', err);
        }

    }

    get maxAllowed(): number {
        const otherAuxiliaryPaymentsAmount = this.booking.paymentHandler.auxiliaryPayments
                                                        .filter(ap => ap.constructor !== this.constructor)
                                                        .sum(ap => ap.usedAmount);
        return Math.max(0, this.booking.bookingData.breakdown.authorizedBalanceDue - otherAuxiliaryPaymentsAmount);
    }

    private _available: Price;
    get available(): Price {
        return this.booking.createPrice(this._available.amount - this.used.amount);
    }


    get used(): Price {
        if(this.useAgencyCredit) {
            return this.booking.createPrice(Math.min(this._available.amount, this.maxAllowed));
        }

        return this.booking.createPrice(0);
    }

    /**
     * IBookingAuxiliaryPayment.usedAmount implementation
     */
    get usedAmount(): number {
        return this.used.amount;
    }


    private _persistFields(): void {
        const fieldsToSave: IOrganizationCreditPaymentMethodFields = {
            useAgencyFunds: this.fields.useAgencyFunds.value ?? false
        }
        this.booking.storage.setJson(BookingSessionStorageKeys.organizationCreditUsed, fieldsToSave);
    }

    private _getPersistedFields(): IOrganizationCreditPaymentMethodFields {
        return this.booking.storage.getJson(BookingSessionStorageKeys.organizationCreditUsed) ?? {
            useAgencyFunds: false
        }
    }

    async addToBooking(): Promise<IAuxiliaryPaymentAddToBookingResult> {
        if(this.used.amount <= 0) {
            return {
                paymentKey: null,
                errorMessage: null
            };
        }

        try {
            const payResult = await this.booking.session.payWithOrganizationCredit({
                amount: this.used.amount,
                currencyCode: this.used.currency
            });
            return {
                paymentKey: payResult.paymentKey,
                errorMessage: null,
            };
        } catch (err) {
            this.services.logger.error('Failed to pay with organization credit', err);

            return {
                paymentKey: null,
                errorMessage: this.services.language.translate('There was an error applying your organization credit to this booking')
            };
        }
    }

}